import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { encodeGeohash } from "../utils/geohash";
import { generateOfficeId, migrateLegacyOffices, syncPrimaryToRoot } from "../utils/officeUtils";
import {
  Add,
  Building,
  CloseCircle,
  Edit2,
  SearchNormal1,
  Star1,
  TickCircle,
  Trash,
} from "iconsax-reactjs";
import Select from "./Select";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./DealerOffices.css";

// Fix default marker icon issue in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const MAX_OFFICES = 10;

export default function DealerOffices({ user }) {
  const [offices, setOffices] = useState([]);
  // False until the first snapshot lands, so the list shows skeleton rows
  // instead of flashing the empty state.
  const [loaded, setLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);

  // Form states
  const [officeName, setOfficeName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [address, setAddress] = useState("");
  const [locality, setLocality] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("Karnataka");
  const [lat, setLat] = useState(12.9716);
  const [lng, setLng] = useState(77.5946);
  const [isPrimary, setIsPrimary] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState("");

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Deleting an office that listings are assigned to: { office, listings }
  // while the dealer picks where those listings move.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [deleting, setDeleting] = useState(false);

  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // Listen to dealer user doc
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      async (snap) => {
        if (!snap.exists()) {
          setLoaded(true);
          return;
        }
        const data = snap.data();

        // Legacy docs are normally converted at sign-in (App.js); this is the
        // fallback if that write failed.
        const currentOffices = await migrateLegacyOffices(user.uid, data).catch(() =>
          Array.isArray(data.offices) ? data.offices : []
        );

        setOffices(currentOffices);
        setLoaded(true);
      },
      (err) => {
        console.error("Offices listener failed:", err);
        setLoaded(true);
      }
    );

    return () => unsub();
  }, [user]);

  // Leaflet map setup for modal
  useEffect(() => {
    if (!isModalOpen) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
      return;
    }

    const timer = setTimeout(() => {
      const container = document.getElementById("office-location-map");
      if (!container || mapRef.current) return;

      const initialLat = lat;
      const initialLng = lng;

      const map = L.map("office-location-map").setView([initialLat, initialLng], 14);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);

      const marker = L.marker([initialLat, initialLng], { draggable: true }).addTo(map);
      markerRef.current = marker;
      mapRef.current = map;

      const onPosChange = async (pos) => {
        setLat(pos.lat);
        setLng(pos.lng);
        await reverseGeocode(pos.lat, pos.lng);
      };

      marker.on("dragend", () => onPosChange(marker.getLatLng()));
      map.on("click", (e) => {
        marker.setLatLng(e.latlng);
        onPosChange(e.latlng);
      });
    }, 200);

    return () => {
      clearTimeout(timer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [isModalOpen]);

  const reverseGeocode = async (latitude, longitude) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18`
      );
      const data = await res.json();
      if (data && data.address) {
        const addr = data.address;
        const resolvedLocality =
          addr.suburb || addr.neighbourhood || addr.village || addr.city_district || "";
        const resolvedCity = addr.city || addr.town || addr.county || "";
        const resolvedState = addr.state || "";
        const street = addr.road || addr.subdivision || "";
        const houseNumber = addr.house_number || "";
        const resolvedAddress =
          [houseNumber, street].filter(Boolean).join(", ") || data.display_name.split(",")[0] || "";

        if (resolvedLocality) setLocality(resolvedLocality);
        if (resolvedCity) setCity(resolvedCity);
        if (resolvedState) setStateName(resolvedState);
        if (resolvedAddress) setAddress(resolvedAddress);
      }
    } catch (e) {
      console.warn("Reverse geocode failed", e);
    }
  };

  const handleMapSearch = async (e) => {
    if (e) e.preventDefault();
    if (!mapSearchQuery.trim()) return;

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery)}&limit=1`
      );
      const data = await res.json();
      if (data && data.length > 0) {
        const latitude = parseFloat(data[0].lat);
        const longitude = parseFloat(data[0].lon);
        setLat(latitude);
        setLng(longitude);
        if (mapRef.current && markerRef.current) {
          mapRef.current.setView([latitude, longitude], 15);
          markerRef.current.setLatLng([latitude, longitude]);
        }
        await reverseGeocode(latitude, longitude);
      } else {
        alert("Location not found. Please try dragging the map marker.");
      }
    } catch (err) {
      console.warn("Location search error", err);
    }
  };

  const openAddModal = () => {
    setEditingOffice(null);
    setOfficeName("");
    setContactNumber(user.contactNumber || user.phoneNumber || "");
    setAddress("");
    setLocality("");
    setCity("");
    setStateName("Karnataka");
    setLat(user.latitude || 12.9716);
    setLng(user.longitude || 77.5946);
    setIsPrimary(offices.length === 0);
    setMapSearchQuery("");
    setError("");
    setMessage("");
    setIsModalOpen(true);
  };

  const openEditModal = (office) => {
    setEditingOffice(office);
    setOfficeName(office.officeName || "");
    setContactNumber(office.contactNumber || "");
    setAddress(office.address || "");
    setLocality(office.locality || "");
    setCity(office.city || "");
    setStateName(office.state || "Karnataka");
    setLat(office.latitude || 12.9716);
    setLng(office.longitude || 77.5946);
    setIsPrimary(!!office.isPrimary);
    setMapSearchQuery("");
    setError("");
    setMessage("");
    setIsModalOpen(true);
  };

  const handleSaveOffice = async (e) => {
    e.preventDefault();
    if (!officeName.trim()) {
      setError("Office name is required.");
      return;
    }
    if (!address.trim() && !locality.trim()) {
      setError("Please provide an address or select a location on the map.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const geohash = encodeGeohash(lat, lng, 9);
      let updatedOffices = [...offices];

      if (editingOffice) {
        // Edit existing
        updatedOffices = updatedOffices.map((o) => {
          if (o.id === editingOffice.id) {
            return {
              ...o,
              officeName: officeName.trim(),
              contactNumber: contactNumber.trim(),
              address: address.trim(),
              locality: locality.trim(),
              city: city.trim(),
              state: stateName.trim(),
              latitude: lat,
              longitude: lng,
              geohash,
              isPrimary: isPrimary,
            };
          }
          return isPrimary ? { ...o, isPrimary: false } : o;
        });
      } else {
        // Add new
        if (updatedOffices.length >= MAX_OFFICES) {
          throw new Error(`Maximum ${MAX_OFFICES} offices allowed.`);
        }
        const newOffice = {
          id: generateOfficeId(),
          officeName: officeName.trim(),
          contactNumber: contactNumber.trim(),
          address: address.trim(),
          locality: locality.trim(),
          city: city.trim(),
          state: stateName.trim(),
          latitude: lat,
          longitude: lng,
          geohash,
          isPrimary: updatedOffices.length === 0 ? true : isPrimary,
        };

        if (newOffice.isPrimary) {
          updatedOffices = updatedOffices.map((o) => ({ ...o, isPrimary: false }));
        }
        updatedOffices.push(newOffice);
      }

      // Ensure at least one primary
      if (!updatedOffices.some((o) => o.isPrimary) && updatedOffices.length > 0) {
        // Copy, don't mutate: these objects are shared with `offices` state.
        updatedOffices = updatedOffices.map((o, i) => (i === 0 ? { ...o, isPrimary: true } : o));
      }

      const rootFields = syncPrimaryToRoot(updatedOffices);
      await setDoc(
        doc(db, "users", user.uid),
        { offices: updatedOffices, ...rootFields, updatedAt: serverTimestamp() },
        { merge: true }
      );

      setMessage("Office location saved successfully.");
      setIsModalOpen(false);
    } catch (err) {
      console.error("Save office failed:", err);
      setError(err.message || "Failed to save office.");
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (officeId) => {
    try {
      const updatedOffices = offices.map((o) => ({
        ...o,
        isPrimary: o.id === officeId,
      }));
      const rootFields = syncPrimaryToRoot(updatedOffices);
      await setDoc(
        doc(db, "users", user.uid),
        { offices: updatedOffices, ...rootFields, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error("Set primary office failed:", err);
      alert("Failed to update primary office.");
    }
  };

  // Removes the office, first moving any listings assigned to it to
  // targetOfficeId ("" = the default main office) so none is left pointing
  // at an office that no longer exists. Listings go first, in chunks under
  // the 500-write batch limit; the office list is written last, so a
  // failure part-way leaves the office (and every listing) still valid.
  const deleteOffice = async (office, listings, targetOfficeId) => {
    setDeleting(true);
    try {
      const LISTINGS_PER_BATCH = 450;
      for (let i = 0; i < listings.length; i += LISTINGS_PER_BATCH) {
        const batch = writeBatch(db);
        listings.slice(i, i + LISTINGS_PER_BATCH).forEach((l) => {
          batch.update(doc(db, "properties", l.id), {
            officeId: targetOfficeId,
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();
      }

      let updatedOffices = offices.filter((o) => o.id !== office.id);
      if (office.isPrimary && updatedOffices.length > 0) {
        updatedOffices = updatedOffices.map((o, i) => (i === 0 ? { ...o, isPrimary: true } : o));
      }
      const rootFields = syncPrimaryToRoot(updatedOffices);
      await setDoc(
        doc(db, "users", user.uid),
        { offices: updatedOffices, ...rootFields, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setPendingDelete(null);
    } catch (err) {
      console.error("Delete office failed:", err);
      alert("Failed to delete office.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDelete = async (office) => {
    if (offices.length <= 1) {
      alert("You must keep at least one office location.");
      return;
    }

    let listings = [];
    try {
      const snap = await getDocs(
        query(
          collection(db, "properties"),
          where("ownerId", "==", user.uid),
          where("officeId", "==", office.id)
        )
      );
      listings = snap.docs
        .filter((d) => d.data().status !== "deleted")
        .map((d) => ({ id: d.id, title: d.data().title || "Untitled listing" }));
    } catch (err) {
      console.error("Failed to check listings for office:", err);
      alert("Couldn't check which listings use this office. Please try again.");
      return;
    }

    if (listings.length === 0) {
      if (
        window.confirm(
          `Are you sure you want to delete "${office.officeName}"?${
            office.isPrimary ? " Another office will become primary." : ""
          }`
        )
      ) {
        await deleteOffice(office, [], "");
      }
      return;
    }

    // Listings route calls and enquiries to their office, so ask where they go.
    // Default to the office that will be primary afterwards.
    const remaining = offices.filter((o) => o.id !== office.id);
    setReassignTo((remaining.find((o) => o.isPrimary) || remaining[0]).id);
    setPendingDelete({ office, listings });
  };

  const atLimit = offices.length >= MAX_OFFICES;
  const showEmpty = loaded && offices.length === 0;
  const closeEditor = () => setIsModalOpen(false);
  const closeDelete = () => !deleting && setPendingDelete(null);

  const tableHead = (
    <thead>
      <tr>
        <th scope="col">Office</th>
        <th scope="col">Address</th>
        <th scope="col">Contact number</th>
        <th scope="col" className="do-col-actions">
          <span className="sr-only">Actions</span>
        </th>
      </tr>
    </thead>
  );

  return (
    <div className="do-container">
      <div className="toolbar do-toolbar">
        <div className="do-summary">
          {loaded && (
            <span className="do-summary-count">
              {offices.length} of {MAX_OFFICES} offices
            </span>
          )}
          <span className="do-summary-note">
            Calls and enquiries for a listing route to the number of the office it is assigned to.
          </span>
        </div>
        <div className="toolbar-spacer" />
        {!showEmpty && (
          <button
            type="button"
            className="btn-primary"
            onClick={openAddModal}
            disabled={atLimit}
            title={atLimit ? `You can add up to ${MAX_OFFICES} offices` : undefined}
          >
            <Add size={18} variant="Linear" />
            <span>Add office</span>
          </button>
        )}
      </div>

      {message && (
        <div className="do-feedback do-feedback-success" role="status">
          <TickCircle size={18} variant="Linear" />
          <span>{message}</span>
        </div>
      )}

      {!loaded ? (
        <div className="table-wrap" aria-busy="true">
          <table className="table do-table">
            {tableHead}
            <tbody>
              {[0, 1, 2].map((i) => (
                <tr key={i} aria-hidden="true">
                  <td><span className="skeleton do-sk do-sk-name" /></td>
                  <td><span className="skeleton do-sk do-sk-address" /></td>
                  <td><span className="skeleton do-sk do-sk-contact" /></td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
          <span className="sr-only">Loading offices</span>
        </div>
      ) : showEmpty ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon">
              <Building size={24} variant="Linear" />
            </span>
            <h3>No offices yet</h3>
            <p>
              Add your branch offices so customers can find your premises and connect with the
              nearest branch. Your first office becomes your primary office.
            </p>
            <button type="button" className="btn-primary do-empty-action" onClick={openAddModal}>
              <Add size={18} variant="Linear" />
              <span>Add your first office</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table do-table">
            {tableHead}
            <tbody>
              {offices.map((office) => {
                const fullAddress = [office.address, office.locality, office.city]
                  .filter(Boolean)
                  .join(", ");
                const hasCoords = office.latitude != null && office.longitude != null;
                return (
                  <tr key={office.id}>
                    <td className="do-cell-office">
                      <div className="do-office-name">
                        <span className="do-office-title">{office.officeName}</span>
                        {office.isPrimary && (
                          <span className="chip chip-primary">
                            <Star1 size={12} variant="Bold" />
                            Primary
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="do-cell-address" data-label="Address">
                      <span className={fullAddress ? "do-address" : "do-address muted"}>
                        {fullAddress || "No address specified"}
                      </span>
                      {hasCoords && (
                        <span className="do-coords">
                          Lat {office.latitude?.toFixed(4)}, Lng {office.longitude?.toFixed(4)}
                        </span>
                      )}
                    </td>
                    <td className="do-cell-contact" data-label="Contact number">
                      {office.contactNumber ? (
                        <span className="do-phone">{office.contactNumber}</span>
                      ) : (
                        <span className="muted">Not added</span>
                      )}
                    </td>
                    <td className="do-cell-actions">
                      <div className="do-row-actions">
                        {!office.isPrimary && (
                          <button
                            type="button"
                            className="btn-ghost do-action"
                            onClick={() => handleSetPrimary(office.id)}
                          >
                            <Star1 size={16} variant="Linear" />
                            <span>Set as primary</span>
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-icon do-icon-action"
                          onClick={() => openEditModal(office)}
                          aria-label={`Edit ${office.officeName || "office"}`}
                          title="Edit office"
                        >
                          <Edit2 size={18} variant="Linear" />
                        </button>
                        {offices.length > 1 && (
                          <button
                            type="button"
                            className="btn-icon do-icon-action do-delete"
                            onClick={() => handleDelete(office)}
                            aria-label={`Delete ${office.officeName || "office"}`}
                            title="Delete office"
                          >
                            <Trash size={18} variant="Linear" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / edit office */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeEditor}>
          <div
            className="modal do-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="do-editor-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="do-editor-title">{editingOffice ? "Edit office" : "Add office"}</h3>
              <button
                type="button"
                className="btn-icon"
                onClick={closeEditor}
                aria-label="Close"
              >
                <CloseCircle size={20} variant="Linear" />
              </button>
            </div>

            <form onSubmit={handleSaveOffice}>
              <div className="modal-body">
                {error && (
                  <div className="do-feedback do-feedback-error" role="alert">
                    {error}
                  </div>
                )}

                <div className="do-form-row">
                  <div className="field">
                    <label className="field-label" htmlFor="do-office-name">
                      Office or branch name <span className="do-required">*</span>
                    </label>
                    <input
                      id="do-office-name"
                      className="input"
                      type="text"
                      value={officeName}
                      onChange={(e) => setOfficeName(e.target.value)}
                      placeholder="e.g. Koramangala branch, Headquarters"
                      required
                    />
                  </div>
                  <div className="field">
                    <label className="field-label" htmlFor="do-office-phone">
                      Branch contact number
                    </label>
                    <input
                      id="do-office-phone"
                      className="input"
                      type="text"
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                      placeholder="e.g. +91 98765 43210"
                    />
                  </div>
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="do-map-search">
                    Find on map
                  </label>
                  <div className="do-map-search">
                    <div className="search-field do-map-search-field">
                      <SearchNormal1 size={16} variant="Linear" />
                      <input
                        id="do-map-search"
                        className="input"
                        type="text"
                        value={mapSearchQuery}
                        onChange={(e) => setMapSearchQuery(e.target.value)}
                        placeholder="Search building, area or landmark"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleMapSearch();
                          }
                        }}
                      />
                    </div>
                    <button type="button" className="btn-secondary" onClick={handleMapSearch}>
                      Search
                    </button>
                  </div>
                </div>

                <div className="do-map-block">
                  <div className="do-map-wrap">
                    <div id="office-location-map" className="do-map" />
                  </div>
                  <span className="field-hint">
                    Click the map or drag the pin to set the exact spot. The address fields below
                    fill in from the pin.
                  </span>
                </div>

                <div className="do-form-row">
                  <div className="field">
                    <label className="field-label" htmlFor="do-office-locality">
                      Locality or area <span className="do-required">*</span>
                    </label>
                    <input
                      id="do-office-locality"
                      className="input"
                      type="text"
                      value={locality}
                      onChange={(e) => setLocality(e.target.value)}
                      placeholder="e.g. Indiranagar"
                      required
                    />
                  </div>
                  <div className="field">
                    <label className="field-label" htmlFor="do-office-city">
                      City <span className="do-required">*</span>
                    </label>
                    <input
                      id="do-office-city"
                      className="input"
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="e.g. Bengaluru"
                      required
                    />
                  </div>
                </div>

                <div className="field">
                  <label className="field-label" htmlFor="do-office-address">
                    Street address
                  </label>
                  <input
                    id="do-office-address"
                    className="input"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="e.g. #102, 100 Feet Rd, 4th Block"
                  />
                </div>

                <label className="do-checkbox-row">
                  <input
                    type="checkbox"
                    checked={isPrimary}
                    onChange={(e) => setIsPrimary(e.target.checked)}
                    disabled={offices.length === 0}
                  />
                  <span className="do-checkbox-text">
                    <span className="do-checkbox-label">Set as primary business office</span>
                    {offices.length === 0 && (
                      <span className="field-hint">Your first office is always the primary office.</span>
                    )}
                  </span>
                </label>
              </div>

              <div className="modal-footer do-modal-footer">
                <button type="button" className="btn-secondary" onClick={closeEditor}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving && <span className="btn-spinner" aria-hidden="true" />}
                  {saving ? "Saving..." : editingOffice ? "Save changes" : "Add office"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete office: reassign its listings first */}
      {pendingDelete && (
        <div className="modal-overlay" onClick={closeDelete}>
          <div
            className="modal do-modal-compact"
            role="dialog"
            aria-modal="true"
            aria-labelledby="do-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="do-delete-title">Delete “{pendingDelete.office.officeName}”?</h3>
              <button
                type="button"
                className="btn-icon"
                onClick={closeDelete}
                aria-label="Close"
                disabled={deleting}
              >
                <CloseCircle size={20} variant="Linear" />
              </button>
            </div>

            <div className="modal-body">
              <p className="do-reassign-intro">
                {pendingDelete.listings.length === 1
                  ? "1 listing is assigned to this office."
                  : `${pendingDelete.listings.length} listings are assigned to this office.`}{" "}
                Choose which office their calls and enquiries should go to before it's removed.
                {pendingDelete.office.isPrimary && " Another office will become primary."}
              </p>

              <ul className="do-reassign-list">
                {pendingDelete.listings.slice(0, 5).map((l) => (
                  <li key={l.id}>{l.title}</li>
                ))}
                {pendingDelete.listings.length > 5 && (
                  <li className="do-reassign-more">
                    and {pendingDelete.listings.length - 5} more
                  </li>
                )}
              </ul>

              <div className="field">
                <label className="field-label" htmlFor="do-reassign-select">
                  Move these listings to
                </label>
                <Select
                  id="do-reassign-select"
                  value={reassignTo}
                  onChange={setReassignTo}
                  disabled={deleting}
                  options={[
                    ...offices
                      .filter((o) => o.id !== pendingDelete.office.id)
                      .map((o) => ({ value: o.id, label: o.officeName || "Office", detail: o.locality })),
                    { value: "", label: "Main office", hint: "Default" },
                  ]}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={() =>
                  deleteOffice(pendingDelete.office, pendingDelete.listings, reassignTo)
                }
                disabled={deleting}
              >
                {deleting && <span className="btn-spinner" aria-hidden="true" />}
                {deleting ? "Moving listings..." : "Move listings and delete office"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
